import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Upload, Trash2, Edit, UserPlus, Search, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminTests() {
  const [tests, setTests] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignFor, setAssignFor] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [search, setSearch] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    const [t, u, a] = await Promise.all([
      api.get("/admin/tests"),
      api.get("/admin/users?role=user"),
      api.get("/admin/assignments"),
    ]);
    setTests(t.data);
    setUsers(u.data);
    setAssignments(a.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tests;
    return tests.filter((t) => (t.title || "").toLowerCase().includes(q));
  }, [tests, search]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/tests/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Test importado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const deleteTest = async (id) => {
    if (!window.confirm("¿Eliminar este test? Se eliminarán intentos y asignaciones.")) return;
    await api.delete(`/admin/tests/${id}`);
    load();
    toast.success("Test eliminado");
  };

  const openAssignDialog = (test) => {
    setAssignFor(test);
    // Pre-select users already assigned to this test so admin sees current state
    const already = assignments
      .filter((a) => a.test_id === test.id)
      .map((a) => a.user_id);
    setSelectedUserIds(already);
    setAssignSearch("");
  };

  const toggleUserSelection = (uid) => {
    setSelectedUserIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid],
    );
  };

  const assign = async () => {
    if (!assignFor) return;
    const already = assignments
      .filter((a) => a.test_id === assignFor.id)
      .map((a) => a.user_id);
    const toAdd = selectedUserIds.filter((u) => !already.includes(u));
    const toRemove = already.filter((u) => !selectedUserIds.includes(u));
    try {
      if (toAdd.length > 0) {
        await api.post("/admin/assignments/bulk-users", {
          test_id: assignFor.id,
          user_ids: toAdd,
        });
      }
      // Remove deselected users (by deleting individual assignments)
      for (const uid of toRemove) {
        const a = assignments.find(
          (x) => x.test_id === assignFor.id && x.user_id === uid,
        );
        if (a) await api.delete(`/admin/assignments/${a.id}`);
      }
      const total = toAdd.length;
      const removed = toRemove.length;
      if (total === 0 && removed === 0) {
        toast.success("Sin cambios");
      } else if (total > 0 && removed > 0) {
        toast.success(`Asignaciones actualizadas (${total} añadidos, ${removed} retirados)`);
      } else if (total > 0) {
        toast.success(`${total} alumno${total !== 1 ? "s" : ""} asignado${total !== 1 ? "s" : ""}`);
      } else {
        toast.success(`${removed} desasignación${removed !== 1 ? "es" : ""}`);
      }
      setAssignFor(null);
      setSelectedUserIds([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unassign = async (aid) => {
    await api.delete(`/admin/assignments/${aid}`);
    load();
  };

  const assignmentsForTest = (testId) => assignments.filter((a) => a.test_id === testId);

  return (
    <div data-testid="admin-tests-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-gray-900">Cursos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Crea cursos y talleres, impórtalos desde CSV/JSON, y asígnalos a usuarios.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.json"
            className="hidden"
            onChange={onImport}
            data-testid="import-test-input"
          />
          <Button
            variant="outline"
            onClick={() => window.open("/", "_blank", "noopener,noreferrer")}
            data-testid="preview-home-tests-button"
            title="Abre la home pública en una pestaña nueva para ver los cambios"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Vista previa home
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-test-button">
            <Upload className="w-4 h-4 mr-2" /> Importar CSV/JSON
          </Button>
          <Link to="/admin/tests/new">
            <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="new-test-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo curso
            </Button>
          </Link>
        </div>
      </div>

      <div className="mb-4 relative w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar cursos…"
          className="pl-9"
          data-testid="tests-search"
        />
      </div>

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Preguntas</TableHead>
              <TableHead>Aprobar</TableHead>
              <TableHead>Tiempo</TableHead>
              <TableHead>Asignados</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Cargando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Sin resultados.</TableCell></TableRow>
            ) : (
              filtered.map((t) => {
                const assigned = assignmentsForTest(t.id);
                return (
                  <TableRow key={t.id} data-testid={`test-row-${t.title}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{t.title}</span>
                        {t.area ? (
                          <span
                            className="inline-flex items-center text-[10px] tracking-[0.05em] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                            data-testid={`area-badge-${t.title}`}
                          >
                            {t.area}
                          </span>
                        ) : null}
                        <span
                          className={
                            t.kind === "final"
                              ? "inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#fdf2d9] text-[#8a6f43] border border-[#e7d5b4]"
                              : "inline-flex items-center text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200"
                          }
                          data-testid={`kind-badge-${t.title}`}
                        >
                          {t.kind === "final" ? "★ Curso · Diploma" : "Taller · Certificado"}
                        </span>
                        {t.study_hours ? (
                          <span className="text-[10px] text-gray-500 font-mono-data">
                            {t.study_hours}h
                          </span>
                        ) : null}
                        {t.price != null && t.price >= 0 ? (
                          <span
                            className="inline-flex items-center text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                            data-testid={`price-badge-${t.title}`}
                          >
                            {t.price === 0
                              ? "Gratis"
                              : new Intl.NumberFormat("es-ES", {
                                  style: "currency",
                                  currency: "EUR",
                                  minimumFractionDigits: Number.isInteger(t.price) ? 0 : 2,
                                }).format(t.price)}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{t.questions?.length || 0}</TableCell>
                    <TableCell className="font-mono-data">{t.pass_percentage}%</TableCell>
                    <TableCell className="font-mono-data text-sm">
                      {t.time_limit_minutes ? (
                        <span className="inline-flex items-center gap-1 text-gray-700">
                          <Clock className="w-3.5 h-3.5" /> {t.time_limit_minutes}m
                        </span>
                      ) : (
                        <span className="text-gray-400">∞</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {assigned.map((a) => {
                          const u = users.find((x) => x.id === a.user_id);
                          return (
                            <span
                              key={a.id}
                              className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded-full"
                            >
                              {u?.name || a.user_id}
                              <button
                                onClick={() => unassign(a.id)}
                                className="text-gray-400 hover:text-red-500 ml-1"
                                title="Desasignar"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                        {assigned.length === 0 && (
                          <span className="text-xs text-gray-400">Ninguno</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openAssignDialog(t)} data-testid={`assign-${t.title}`}>
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Asignar
                      </Button>
                      <Link to={`/admin/tests/${t.id}/edit`}>
                        <Button variant="ghost" size="sm" data-testid={`edit-${t.title}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => deleteTest(t.id)} data-testid={`delete-${t.title}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!assignFor} onOpenChange={(o) => { if (!o) { setAssignFor(null); setSelectedUserIds([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asignar test: {assignFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-gray-500">
              Marca los alumnos que deben tener este test asignado. Al guardar, se añadirán los nuevos y se quitarán los desmarcados.
            </p>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                placeholder="Buscar alumno por nombre o email…"
                className="pl-9"
                data-testid="assign-search-input"
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">
                {selectedUserIds.length} de {users.length} alumnos seleccionados
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="text-brand hover:underline"
                  onClick={() => setSelectedUserIds(users.map((u) => u.id))}
                  data-testid="assign-select-all"
                >
                  Seleccionar todos
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={() => setSelectedUserIds([])}
                  data-testid="assign-clear-all"
                >
                  Limpiar
                </button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {users
                .filter((u) => {
                  const q = assignSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    (u.name || "").toLowerCase().includes(q) ||
                    (u.email || "").toLowerCase().includes(q) ||
                    (u.dni || "").toLowerCase().includes(q)
                  );
                })
                .map((u) => {
                  const checked = selectedUserIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                        checked ? "bg-brand-soft/40" : ""
                      }`}
                      data-testid={`assign-user-row-${u.email}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-brand-ink truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      {u.dni ? (
                        <span className="text-[10px] font-mono-data text-gray-400">{u.dni}</span>
                      ) : null}
                    </label>
                  );
                })}
              {users.length === 0 && (
                <p className="text-center text-xs text-gray-400 py-6">Sin alumnos disponibles.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={assign}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="confirm-assign"
            >
              Guardar asignaciones
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6 text-xs text-gray-500 leading-relaxed bg-white border border-gray-200 rounded-lg p-4">
        <p className="font-medium text-gray-700 mb-1">Formato de importación</p>
        <p>
          <span className="font-mono-data">JSON</span>:{" "}
          <code className="font-mono-data">{`{ title, description, pass_percentage, time_limit_minutes, questions: [{ text, type: "single"|"multiple"|"truefalse", options: ["..."], correct: [0,1] }] }`}</code>
        </p>
        <p className="mt-1">
          <span className="font-mono-data">CSV</span> columnas:{" "}
          <code className="font-mono-data">question, type, options (separadas por |), correct (índices separados por coma)</code>
        </p>
      </div>
    </div>
  );
}
